var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import appConfig from '../helpers/appConfig';
import * as platform from '../helpers/platformApi';
import { finaliseLogout } from './user';
let allowNewCall = true;
export const startCall = createAsyncThunk('calls/startCall', (params, thunkAPI) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    const { number, type, name, description, email } = params;
    // If the allowNewCall flag is false, don't allow a call to be started
    if (!allowNewCall)
        return;
    setTimeout(() => {
        allowNewCall = true;
    }, 1200);
    const { extension } = appConfig;
    const { calls, client } = thunkAPI.getState();
    if (!extension) {
        return;
    }
    const ringingOutobundCall = calls.find((c) => c.ringing && c.direction === 'OUT');
    // If there's a call ringing, don't allow another call to be started
    if (ringingOutobundCall)
        return;
    const outboundCallOptions = {
        mediaConstraints: {
            audio: true,
            video: false,
        },
        rtcOfferConstraints: {
            offerToReceiveVideo: 0,
        },
        extraHeaders: [],
    };
    const callerId = client.selectedCallerId;
    if (callerId === null || callerId === void 0 ? void 0 : callerId.number) {
        outboundCallOptions.extraHeaders.push(`P-Preferred-Identity: <sip:${callerId.number.replace(/\s/g, '')}@sipcentric.com>`);
    }
    const call = extension.dial(number, outboundCallOptions);
    const activeCallState = calls.find((c) => c.active);
    const callInfo = {
        type,
        number,
        name,
        description,
        email,
        direction: 'OUT',
    };
    // Set up event handlers
    (_a = call.on) === null || _a === void 0 ? void 0 : _a.call(call, 'connecting', () => thunkAPI.dispatch(createCall(Object.assign({ id: call.session.id }, callInfo))));
    (_b = call.on) === null || _b === void 0 ? void 0 : _b.call(call, 'progress', (e) => {
        if (e.response && e.response.status_code === 183) {
            thunkAPI.dispatch(callRingingStop({ id: call.session.id }));
        }
        thunkAPI.dispatch(callRinging({ id: call.session.id }));
    });
    (_c = call.on) === null || _c === void 0 ? void 0 : _c.call(call, 'accepted', () => thunkAPI.dispatch(callAnswered({ id: call.session.id })));
    (_d = call.on) === null || _d === void 0 ? void 0 : _d.call(call, 'ended', () => thunkAPI.dispatch(callRemoved({ id: call.session.id })));
    (_e = call.on) === null || _e === void 0 ? void 0 : _e.call(call, 'failed', (err) => {
        console.error('Call failed', err);
        thunkAPI.dispatch(callRemoved({ id: call.session.id }));
    });
    (_f = call.on) === null || _f === void 0 ? void 0 : _f.call(call, 'hold', () => thunkAPI.dispatch(callHold({ id: call.session.id })));
    (_g = call.on) === null || _g === void 0 ? void 0 : _g.call(call, 'unhold', () => thunkAPI.dispatch(callUnhold({ id: call.session.id })));
    (_h = call.on) === null || _h === void 0 ? void 0 : _h.call(call, 'muted', () => thunkAPI.dispatch(callMuted({ id: call.session.id })));
    (_j = call.on) === null || _j === void 0 ? void 0 : _j.call(call, 'unmuted', () => thunkAPI.dispatch(callUnmuted({ id: call.session.id })));
    (_k = call.on) === null || _k === void 0 ? void 0 : _k.call(call, 'refersucceeded', () => thunkAPI.dispatch(callRemoved({ id: call.session.id })));
    (_l = call.on) === null || _l === void 0 ? void 0 : _l.call(call, 'referfailed', () => thunkAPI.dispatch(cancelTransfer({ id: call.session.id })));
    (_m = call.on) === null || _m === void 0 ? void 0 : _m.call(call, 'newDTMF', (e) => {
        const { tone } = e.dtmf;
        if (e.originator === 'local') {
            thunkAPI.dispatch(sentDTMF({ id: call.session.id, tone }));
        }
    });
    allowNewCall = false;
    if (activeCallState) {
        const activeCallId = activeCallState.id;
        const activeCall = extension.getOngoingCall(activeCallId);
        if (!activeCall.isOnHold) {
            activeCall.once('hold', () => {
                thunkAPI.dispatch(callDeactivate({ id: activeCallId }));
                call.invite();
            });
            // thunkAPI.dispatch(holdingCall({id:activeCallId}));
            activeCall.hold();
        }
        else {
            thunkAPI.dispatch(callDeactivate({ id: activeCallId }));
            call.invite();
        }
    }
    else {
        call.invite();
    }
}));
export const endCall = createAsyncThunk('calls/endCall', (id, thunkAPI) => __awaiter(void 0, void 0, void 0, function* () {
    const { extension } = appConfig;
    if (!extension) {
        return;
    }
    const call = extension.getOngoingCall(id);
    thunkAPI.dispatch(endingCall({ id }));
    if (!call) {
        console.warn(`Ongoing call with id ${id} doesn't exist`);
        Sentry.captureException(new Error(`Ongoing call doesn't exist (trying to end)`));
        // end the call not to leave the user with unresponsive UI
        thunkAPI.dispatch(callRemoved({ id }));
    }
    else {
        call.bye();
    }
    try {
        platform.closeNotification(id);
    }
    catch (err) {
        console.debug('Error closing notification: ', err);
    }
}));
// FIXME doesnt have to be thunk, maybe also others here?
export const holdCall = createAsyncThunk('calls/holdCall', (id) => __awaiter(void 0, void 0, void 0, function* () {
    const { extension } = appConfig;
    if (!extension) {
        return;
    }
    const call = extension.getOngoingCall(id);
    // thunkAPI.dispatch(holdingCall({id}));
    call.hold();
}));
export const holdCallAndDeactivate = createAsyncThunk('calls/holdCallAndDeactivate', (id, thunkAPI) => __awaiter(void 0, void 0, void 0, function* () {
    const { extension } = appConfig;
    if (!extension) {
        return;
    }
    const call = extension.getOngoingCall(id);
    if (call.isOnHold) {
        thunkAPI.dispatch(callDeactivate({ id: call.session.id }));
    }
    else {
        // thunkAPI.dispatch(holdingCall({id}));
        call.once('hold', () => thunkAPI.dispatch(callDeactivate({ id: call.session.id })));
        call.hold();
    }
}));
export const unholdCall = createAsyncThunk('calls/unholdCall', (id, thunkAPI) => __awaiter(void 0, void 0, void 0, function* () {
    const { extension } = appConfig;
    if (!extension) {
        return;
    }
    const call = extension.getOngoingCall(id);
    const { calls } = thunkAPI.getState();
    const callToUnhold = calls.find((c) => c.id === id);
    const activeCall = calls.find((c) => c.active);
    // If the call we're unholding is the active call
    // or there isn't an active call then that's
    // easy, just unhold it.
    // Otherwise, we need to also remember to
    // deactivate the active call as we unhold
    if ((callToUnhold === null || callToUnhold === void 0 ? void 0 : callToUnhold.active) || !activeCall) {
        // thunkAPI.dispatch(unholdingCall({id}));
        call.unhold();
    }
    else {
        // thunkAPI.dispatch(unholdingCall({id}));
        call.once('unhold', () => thunkAPI.dispatch(callDeactivate({ id: activeCall.id })));
        call.unhold();
    }
}));
export const muteCall = createAsyncThunk('calls/muteCall', (id) => __awaiter(void 0, void 0, void 0, function* () {
    const { extension } = appConfig;
    if (!extension) {
        return;
    }
    const call = extension.getOngoingCall(id);
    // thunkAPI.dispatch(mutingCall({id}));
    call.mute();
}));
export const unmuteCall = createAsyncThunk('calls/unmuteCall', (id) => __awaiter(void 0, void 0, void 0, function* () {
    const { extension } = appConfig;
    if (!extension) {
        return;
    }
    const call = extension.getOngoingCall(id);
    // thunkAPI.dispatch(unmutingCall({id}));
    call.unmute();
}));
export const transferCall = createAsyncThunk('calls/transferCall', (params, thunkAPI) => __awaiter(void 0, void 0, void 0, function* () {
    const { extension } = appConfig;
    if (!extension || !params.id) {
        return;
    }
    const call = extension.getOngoingCall(params.id);
    thunkAPI.dispatch(transferringCall({ id: params.id }));
    call.refer(params.number);
}));
export const attendedTransferCall = createAsyncThunk('calls/attendedTransferCall', (id, thunkAPI) => __awaiter(void 0, void 0, void 0, function* () {
    const { extension } = appConfig;
    if (!extension) {
        return;
    }
    const activeCall = extension.getActiveCall();
    const activeCallUser = activeCall.session.remote_identity.uri.user;
    const callToTransfer = extension.getOngoingCall(id);
    const options = {
        replaces: activeCall.session,
    };
    thunkAPI.dispatch(transferringCall({ id: activeCall.session.id }));
    callToTransfer.refer(activeCallUser, options);
}));
export const sendDTMF = createAsyncThunk('calls/sendDTMF', (params) => __awaiter(void 0, void 0, void 0, function* () {
    const { extension } = appConfig;
    if (!extension) {
        return;
    }
    const call = extension.getOngoingCall(params.id);
    // thunkAPI.dispatch(sendingDTMF({id:params.id}));
    call.sendDTMF(params.tone);
}));
export const incomingCall = createAsyncThunk('calls/incomingCall', (call, thunkAPI) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    const { session } = call;
    const callFrom = session._request.from.uri.user;
    // Defaults
    let type = 'phonebookentry';
    let number = callFrom;
    let name = callFrom;
    let description = '';
    let email = '';
    const state = thunkAPI.getState();
    const { contacts: { phonebook, phones, virtuals }, } = state;
    const timeout = (_a = state.extension.extension) === null || _a === void 0 ? void 0 : _a.timeout;
    const contactsArr = [...phones, ...virtuals, ...phonebook];
    const contact = contactsArr.find((c) => {
        const contactNumber = c.type === 'phonebookentry' ? c.phoneNumber : c.shortNumber;
        return contactNumber === callFrom;
    });
    if (contact) {
        const contactNumber = contact.type === 'phonebookentry'
            ? contact.phoneNumber
            : contact.shortNumber;
        type = contact.type;
        number = contactNumber;
        name = contact.name;
        description = contactNumber;
        // @ts-expect-error
        email = contact.email;
    }
    try {
        const { favicons } = thunkAPI.getState().branding;
        // const x16 = (favicons || []).find(
        //   (fi) => fi.name.toLowerCase() === 'favicon-16x16.png',
        // )?.location;
        const x32 = (_b = (favicons || []).find((fi) => fi.name.toLowerCase() === 'favicon-32x32.png')) === null || _b === void 0 ? void 0 : _b.location;
        platform.notify(session.id, `Incoming call from ${callFrom}`, '', {
            icon: x32,
            timeout,
        });
    }
    catch (err) {
        console.debug('Error sending notification: ', err);
    }
    const callInfo = {
        type,
        number,
        name,
        description,
        email,
        direction: 'IN',
        tags: call.tags,
    };
    // Create call
    thunkAPI.dispatch(createCall(Object.assign({ id: session.id }, callInfo)));
    const endSession = () => {
        try {
            platform.closeNotification(session.id);
        }
        catch (err) {
            console.debug('Error closing notification: ', err);
        }
        thunkAPI.dispatch(callRemoved({ id: session.id }));
    };
    session.on('progress', () => thunkAPI.dispatch(callRinging({ id: session.id })));
    session.on('accepted', () => thunkAPI.dispatch(callAnswered({ id: session.id })));
    session.on('ended', endSession);
    session.on('failed', endSession);
    session.on('hold', () => thunkAPI.dispatch(callHold({ id: session.id })));
    session.on('unhold', () => thunkAPI.dispatch(callUnhold({ id: session.id })));
    session.on('muted', () => thunkAPI.dispatch(callMuted({ id: session.id })));
    session.on('unmuted', () => thunkAPI.dispatch(callUnmuted({ id: session.id })));
    session.on('refersucceeded', () => thunkAPI.dispatch(callRemoved({ id: session.id })));
    session.on('referfailed', () => thunkAPI.dispatch(cancelTransfer({ id: session.id })));
    session.on('newDTMF', (e) => {
        const { tone } = e.dtmf;
        if (e.originator === 'local') {
            thunkAPI.dispatch(sentDTMF({ id: session.id, tone }));
        }
    });
}));
export const answerCall = createAsyncThunk('calls/answerCall', (id, thunkAPI) => __awaiter(void 0, void 0, void 0, function* () {
    const { extension } = appConfig;
    if (!extension) {
        return;
    }
    const { calls } = thunkAPI.getState();
    const callState = calls.find((c) => c.id === id);
    const answering = callState === null || callState === void 0 ? void 0 : callState.answering;
    if (answering) {
        return;
    }
    const call = extension.getOngoingCall(id);
    const answer = () => {
        try {
            platform.closeNotification(id);
        }
        catch (err) {
            console.debug('Error closing notification: ', err);
        }
        if (!call) {
            console.warn(`Ongoing call with id ${id} doesn't exist`);
            Sentry.captureException(new Error(`Ongoing call doesn't exist (trying to answer)`));
            // end the call not to leave the user with unresponsive UI
            thunkAPI.dispatch(callRemoved({ id }));
        }
        else {
            thunkAPI.dispatch(answeringCall({ id }));
            call.connect();
        }
    };
    const activeCallState = calls.find((c) => c.active);
    if (activeCallState) {
        const activeCallId = activeCallState.id;
        const activeCall = extension.getOngoingCall(activeCallId);
        if (!activeCall.isOnHold) {
            activeCall.once('hold', () => {
                thunkAPI.dispatch(callDeactivate({ id: activeCallId }));
                answer();
            });
            // thunkAPI.dispatch(holdingCall({id:activeCallId}));
            activeCall.hold();
        }
        else {
            thunkAPI.dispatch(callDeactivate({ id: activeCallId }));
            answer();
        }
    }
    else {
        answer();
    }
}));
const modifyCallById = (state, newCall) => {
    const call = state.find((c) => c.id === newCall.id);
    if (!call) {
        return;
    }
    Object.keys(newCall).forEach((key) => {
        // FIXME
        // @ts-expect-error
        call[key] = newCall[key];
    });
};
const callsSlice = createSlice({
    name: 'calls',
    initialState: [],
    reducers: {
        createCall: (state, action) => {
            const { tags, direction } = action.payload;
            state.push(Object.assign(Object.assign({}, action.payload), { tags: Array.isArray(tags) ? tags : [], onHold: false, muted: false, active: direction === 'OUT', answered: false, answering: false, ringing: false, ending: false, transferInitiated: false, isTransferring: false, forceStopRinging: false, diallerVisible: false, tones: [], created: Date.now() }));
        },
        callRinging: (state, action) => {
            modifyCallById(state, { id: action.payload.id, ringing: true });
        },
        callRingingStop: (state, action) => {
            modifyCallById(state, { id: action.payload.id, forceStopRinging: true });
        },
        callAnswered: (state, action) => {
            modifyCallById(state, {
                id: action.payload.id,
                ringing: false,
                active: true,
                answered: true,
                answering: false,
                started: Date.now(),
            });
        },
        callHold: (state, action) => {
            modifyCallById(state, {
                id: action.payload.id,
                onHold: true,
                active: true,
            });
        },
        callDeactivate: (state, action) => {
            modifyCallById(state, {
                id: action.payload.id,
                onHold: true,
                active: false,
            });
        },
        callUnhold: (state, action) => {
            modifyCallById(state, {
                id: action.payload.id,
                onHold: false,
                active: true,
            });
        },
        callMuted: (state, action) => {
            modifyCallById(state, { id: action.payload.id, muted: true });
        },
        callUnmuted: (state, action) => {
            modifyCallById(state, { id: action.payload.id, muted: false });
        },
        endingCall: (state, action) => {
            modifyCallById(state, { id: action.payload.id, ending: true });
        },
        answeringCall: (state, action) => {
            modifyCallById(state, { id: action.payload.id, answering: true });
        },
        initiateTransfer: (state, action) => {
            modifyCallById(state, { id: action.payload.id, transferInitiated: true });
        },
        cancelTransfer: (state, action) => {
            modifyCallById(state, {
                id: action.payload.id,
                transferInitiated: false,
            });
        },
        transferringCall: (state, action) => {
            modifyCallById(state, {
                id: action.payload.id,
                isTransferring: true,
                transferInitiated: false,
            });
        },
        sentDTMF: (state, action) => {
            const call = state.find((c) => c.id === action.payload.id);
            if (!call) {
                return;
            }
            call.tones.push(action.payload.tone);
        },
        showDialler: (state, action) => {
            modifyCallById(state, { id: action.payload.id, diallerVisible: true });
        },
        hideDialler: (state, action) => {
            modifyCallById(state, { id: action.payload.id, diallerVisible: false });
        },
        callRemoved: (state, action) => {
            return state.filter((s) => s.id !== action.payload.id);
        },
    },
    extraReducers: (builder) => {
        builder.addCase(finaliseLogout.fulfilled, () => []);
    },
});
export const { createCall, callRinging, callRingingStop, callAnswered, callHold, callDeactivate, callUnhold, callMuted, callUnmuted, endingCall, answeringCall, initiateTransfer, cancelTransfer, transferringCall, sentDTMF, showDialler, hideDialler, callRemoved, } = callsSlice.actions;
export default callsSlice.reducer;
// case HOLDING_CALL: {
//   return state;
// }
// case UNHOLDING_CALL: {
//   return state;
// }
// case MUTING_CALL: {
//   return state;
// }
// case UNMUTING_CALL: {
//   return state;
// }
// case SENDING_DTMF: {
//   return {
//     ...state,
//   };
// }
